import React, { useMemo, useState } from 'react';
import { history, getLocale } from 'umi';
import { Table, Button, Modal, notification, PagingQuery } from 'tea-component';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import { TopchartPageId } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import TopchartTable from '@intelligence/components/TableComponents/TopchartTable';
import { pageAble } from '@intelligence/utils/commonUtils';
import { MetricsTableCloums } from '@intelligence/pages/TopCharts/_config/TableCloums/MetricsTableCloums';
import {
  allMetricsOptions,
  ampereMetricOptions,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { sortTableByMetric } from '@intelligence/utils/tableUtils';
import { iconSortFun } from '@intelligence/utils/common';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import DatabrainBubbleTip from '@intelligence/components/TableComponents/components/FirstRow/DatabrainBubbleTip';
import SvgIcon from '@home/components/SvgIcon';
import './index.scss';
import { RenderType } from '@intelligence/pages/_config/topchartsInterface';
import NoData from '@intelligence/components/NoData';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { getGamesColorMap } from '@intelligence/pages/Compare/PcConsoleGamesBeta/utils';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { formatDateRangeWhenAlignDateModePlus } from '@intelligence/pages/Compare/utils';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';

const defaultPageSize = 10;
const pageSizeOptions = [10, 20, 50];
const defaultPageSortAttr = {
  currentSortValue: 'desc',
  currentSortMetric: 'game',
  currentRatioType: '0',
};
const { pageable } = Table.addons;

interface IDateRange {
  startDate?: string;
  endDate?: string;
}

interface ITableModuleProps extends IBaseProps {
  responseQueryDate?: IDateRange;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
}

const getDateOfCompareData = (isAlignToRelease: boolean, responseQueryDate?: IDateRange, fetchParams?: IRecord): IDateRange => {
  if (isAlignToRelease) {
    return { startDate: '', endDate: '' };
  }

  const startDate = responseQueryDate?.startDate;
  const endDate = responseQueryDate?.endDate;

  if (startDate && endDate) {
    return { startDate, endDate };
  }

  return {
    startDate: fetchParams?.start_date,
    endDate: fetchParams?.end_date,
  };
};

const TableModule: React.FC<ITableModuleProps> = (props) => {
  const { formatMessage } = useIntlExtend();
  const {
    entityType = 'combine',
    loading,
    isAlignToRelease,
    fetchParams = {},
    tableData,
    onChange,
    currentMetric,
    paramsRegionOption,
    gamesColorMap,
    setGamesColorMap,
    tableSetMax,
    responseQueryDate,
    onMetricExplanationClick,
  } = props;

  const locale = getLocale();
  const isCN = locale === 'zh-CN';
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [pageSortTable, setPageSortTable] = useState<any[]>(tableData);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [sortTableData, setSortTableData] = useState(tableData);
  const [deleteItem, setDeleteItem] = useState<Record<string, any>>({});
  const [pageSortAttr, setPageSortAttr] = useState<any>({
    ...defaultPageSortAttr,
    currentSortMetric: currentMetric,
  });
  const { startDate: dataCompareStartDate, endDate: dataCompareEndDate } = getDateOfCompareData(isAlignToRelease, responseQueryDate, fetchParams);

  const splitor = 'dbi_metric_';

  // 处理时间粒度的特定指标
  const needProcessMetricsValue = [
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rolling_d1',
    'ampere_rolling_d7',
    'ampere_rolling_d14',
    'ampere_rolling_d28',
    'ampere_rolling_d60',
  ];

  const columnList: any[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { source_metrics, granularity } = fetchParams;
    const objValue = Object.values(source_metrics).flat();
    let filterMetrics = allMetricsOptions.filter((item: any) => {
      return objValue.includes(item?.value);
    });
    filterMetrics = filterMetrics?.map((item: any) => {
      return {
        ...item,
        text: isCN ? item?.text : item[granularity] || item.text,
      };
    });
    return filterMetrics;
  }, [fetchParams]);

  const tableTotal = sortTableData?.length || 0;
  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    // onTablePagingChange(e);
    const pageList = pageAble(
      sortTableData,
      e.pageIndex || 1,
      e.pageSize || defaultPageSize,
    );
    setPageSortTable(combineTable(pageList));
  };
  const combineTable = (pageList: any[]) => {
    return [...(pageList || [])];
  };
  const onSortChange = (e: any) => {
    setPageSortAttr({
      currentSortValue: e.sortVal,
      currentSortMetric: e.sortMetric,
      currentRatioType: e.ratioType,
    });

    const sortTable = sortTableByMetric({
      tableData: sortTableData,
      isRank: false,
      sortParams: e,
      metricProperty: 'metric',
      growthProperty: 'growth_ratio',
      listProperty: 'metric_value_list',
    });

    // 排序后更新游戏对应颜色 map
    const gamesColorMap = getGamesColorMap(sortTable);
    setGamesColorMap(gamesColorMap);

    const pageList: any[] = pageAble(sortTable, pageIndex, pageSize);
    setPageSortTable(combineTable(pageList));
    setSortTableData(sortTable.slice(0));
  };
  const { currentSortValue, currentSortMetric, currentRatioType } =
    pageSortAttr || {};
  const handleClickDeleteBtn = async (cvm: any) => {
    setDeleteItem(cvm);
    setModalVisible(true);
  };
  const handleConfirm = () => {
    const { id } = deleteItem;
    const newTable = sortTableData.filter((item: any) => item?.id !== id);
    // const newTable = tableSetMax(filterTable, pageSortAttr?.currentMetric);
    let pageList: any[] = pageAble(newTable, pageIndex, pageSize);
    if (!pageList?.length && pageIndex > 1) {
      pageList = pageAble(newTable, pageIndex - 1, pageSize);
      setPageIndex(pageIndex - 1);
    }
    onChange(id);
    setPageSortTable(combineTable(pageList));
    setSortTableData(newTable.slice(0));
    setModalVisible(false);
    setDeleteItem({});
    notification.success({
      description: formatMessage({
        id: 'dbi_operation_successful_tips',
      }),
    });
  };
  const onClickGame = (gameId: string) => {
    const linkUrl = getDetailGamePathCommon({
      entityType,
      gameId,
      tab: 'stats',
      subTab: '',
    });
    history.push(linkUrl);
  };
  const getMetricColumns = () => {
    const metricColumns: any[] = [];

    columnList?.forEach((t: any) => {
      // const isAmpere = ampereMetricOptions?.find(
      //   (item) => item.value === t?.value,
      // );

      // metricColumns.push(
      //   MetricsTableCloums({
      //     maxWidth: 650,
      //     /** MetricsTable页只有降序功能 */
      //     onlySimpleSort: false,
      //     // renderType: RenderType.PC_CONSOLE_COMPARE,
      //     renderType:
      //       isAmpere && t?.renderType
      //         ? t?.renderType
      //         : RenderType.PC_CONSOLE_COMPARE,
      //     width: t?.width > 220 ? t?.width : 220,
      //     width_cn: t?.width_cn > 220 ? t?.width_cn : 220,
      //     noWrapWidth: t?.noWrapWidth,
      //     noWrapWidthCN: t?.noWrapWidthCN,
      //     columnLength: columnList?.length,
      //     icon: t?.icon,
      //     dataSource: t?.dataSource, // source === 'ogdb' ? 'databarin' : source,
      //     title: /* t?.textDetail ??*/ t?.text,
      //     textContent: t?.infoDetail ?? t?.info,
      //     textContentSuffix: t?.infoSuffix,
      //     tipText: t?.iconInfo,
      //     key: `${t?.value}`,
      //     sortMetric: t?.value,
      //     currentSortValue,
      //     currentSortKey: `${currentSortMetric}_${currentRatioType}`,
      //     onSortChage: onSortChange,
      //     dateType: fetchParams?.granularity,
      //     formatMessage,
      //     isNoDropdown: tableTotal === 1, // 只有一条数据时不展示排序图标
      //     isDetailPage: true,
      //     dataSourceType: t?.dataSourceType || t?.value,
      //     alignRight: true,
      //     getMetricValueOfCvm: (cvm, metricKey) => {
      //       const metric_value_list = cvm?.metric_value_list || [];
      //       if (metric_value_list?.length > 0) {
      //         return metric_value_list.find(
      //           (item: any) => item?.metric === metricKey,
      //         );
      //       }
      //       return '';
      //     },
      //     entityType,
      //     gamesColorMap,
      //   }),
      // );
      const isAmpere = ampereMetricOptions?.find(
        (item) => item.value === t?.value,
      );
      const isMonth = fetchParams?.granularity === 'monthly';
      const mid = isMonth ? 'monthly_' : '';
      const textStrArr = t?.text?.split(splitor);
      const rawMetricText = textStrArr?.[textStrArr?.length - 1];
      const _textContent = t?.infoDetail ?? t?.info;
      const textContentStrArr = _textContent?.split(splitor);
      const rawTextContent = textContentStrArr?.[textContentStrArr?.length - 1];

      const pushItem = MetricsTableCloums({
        maxWidth: 650,
        /** MetricsTable页只有降序功能 */
        onlySimpleSort: false,
        // renderType: RenderType.PC_CONSOLE_COMPARE,
        renderType:
          isAmpere && t?.renderType
            ? t?.renderType
            : RenderType.PC_CONSOLE_COMPARE,
        // width: t?.width > 220 ? t?.width : 220,
        // width_cn: t?.width_cn > 220 ? t?.width_cn : 220,
        noWrapWidth: t?.noWrapWidth,
        noWrapWidthCN: t?.noWrapWidthCN,
        columnLength: columnList?.length,
        icon: t?.icon,
        dataSource: t?.dataSource, // source === 'ogdb' ? 'databarin' : source,
        // title: /* t?.textDetail ??*/ t?.text,
        title:
          isMonth && isAmpere && needProcessMetricsValue.includes(t?.value)
            ? `${splitor}${mid}${rawMetricText}`
            : t?.text,
        // textContent: t?.infoDetail ?? t?.info,
        textContent:
          isMonth && isAmpere && needProcessMetricsValue.includes(t?.value)
            ? `${splitor}${mid}${rawTextContent}`
            : _textContent,
        textContentSuffix: t?.infoSuffix,
        tipText: t?.iconInfo,
        key: `${t?.value}`,
        sortMetric: t?.value,
        currentSortValue,
        currentSortKey: `${currentSortMetric}_${currentRatioType}`,
        onSortChage: onSortChange,
        dateType: fetchParams?.granularity,
        startDate: dataCompareStartDate,
        endDate: dataCompareEndDate,
        formatMessage,
        isNoDropdown: tableTotal === 1, // 只有一条数据时不展示排序图标
        isDetailPage: true,
        dataSourceType: t?.dataSourceType || t?.value,
        alignRight: true,
        getMetricValueOfCvm: (cvm, metricKey) => {
          const metric_value_list = cvm?.metric_value_list || [];
          if (metric_value_list?.length > 0) {
            return metric_value_list.find(
              (item: any) => item?.metric === metricKey,
            );
          }
          return '';
        },
        entityType,
        gamesColorMap,
        onMetricExplanationClick,
      });

      // console.debug('pushItem', { pushItem, t });

      metricColumns.push(pushItem);
    });

    // console.debug('getMetric', { metricColumns, columnList, RenderType, fetchParams });
    return metricColumns;
  };
  const getFirstColumns = () => {
    const result = [
      {
        key: 'gameTitle',
        width: 350,
        fixed: 'left',
        header: (
          <div className="dbi-firstRow-flex">
            <div style={{ marginRight: 8 }}>
              {formatMessage({ id: 'dbi_top_gametitle' })}
            </div>
          </div>
        ),
        render: (cvm: any) => {
          // 获取entity的icon，排序先pc再console
          const order = ['mobile', 'pc', 'console'];
          const entityIcons: string[] = iconSortFun(
            Array.from(new Set(cvm?.entity_icon)),
            order,
          );
          const releaseTimeFormatted = formatReleaseTime(locale, {
            isSpecialReleaseTime: cvm?.is_special_release_time,
            releaseTime: cvm?.release_time,
            releaseTimeEN: cvm?.release_time_en,
            releaseTimeCN: cvm?.release_time_cn,
          });

          return (
            <div className={'dbi-table-inner-box'}>
              <FirstRow
                id={''}
                onClick={() => {
                  onClickGame(cvm?.id);
                }}
                cvm={{
                  imgType: entityType,
                  img: cvm?.cover,
                  gametitle: cvm?.name,
                  countryIcon: '',
                  text: '',
                  databrainIconTip: <DatabrainBubbleTip />,
                  icon: null,
                  entityIcon:
                    entityType === 'combine' && cvm?.entity_icon?.length ? (
                      <>
                        {entityIcons?.map((plat: string) => (
                          <a
                            className="dbi-pcconsole-table-firstrow"
                            key={cvm?.plat}
                          >
                            <SvgIcon
                              className="dbi-pcconsole-table-entityicon"
                              type={plat === 'pc' ? 'monitor' : 'game'}
                            />
                          </a>
                        ))}
                      </>
                    ) : null,
                }}
                iconRightDom={
                  <div className="dbi-compare-game-release-date">
                    <span>{formatMessage({ id: 'dbi_release_date_full_release' })}:</span>&nbsp;
                    <span>{releaseTimeFormatted}</span>
                  </div>
                }
              />
              <SvgIcon
                className="dbi-table-inner-close-icon"
                type="clear-icon"
                onClick={() => handleClickDeleteBtn(cvm)}
              />
            </div>
          );
        },
      },
    ];

    if (isAlignToRelease) {
      const dateRangeColumn = {
        key: 'dateRange',
        width: 224,
        fixed: 'left',
        header: (
          <div className="dbi-firstRow-flex">
            <div style={{ marginRight: 8 }}>{formatMessage({ id: 'dbi_date_range' })}</div>
          </div>
        ),
        render: (cvm: any) => {
          return <div>{formatDateRangeWhenAlignDateModePlus(locale, fetchParams, cvm)}</div>;
        },
      };
      result.push(dateRangeColumn);
    }

    return result;
  };
  const getAllColumns = (): any[] => {
    // 去掉空白列
    return [...getFirstColumns(), ...getMetricColumns()];
  };
  const getDownloadFetchParams = () => {
    // 使用排序后的表格数据顺序，确保下载的excel文件中游戏顺序与表格一致
    const sortedIds = sortTableData?.map((item: any) => item?.id) || fetchParams?.ids;
    const paramsTmp: {
      [key: string]: any;
    } = {
      ...fetchParams,
      isAlignToRelease,
      regionOptions: paramsRegionOption,
      device: fetchParams?.platforms,
      fp: { ...fetchParams, ids: sortedIds },
      region_type: 'market',
      regions: ['global'],
      date_type: fetchParams?.granularity,
      segment_list: ['All'],
      segment: 'All',
      subSegment: 'allMetrics',
      unified_ids: sortedIds,
      currentMetric: 'allMetrics',
      isCountriesPage: false,
    };
    return paramsTmp;
  };

  const exportTableCom = (
    <ExportTable
      buttonType="icon"
      entityType={entityType}
      buttonClassName={'trends-table-download-icon'}
      tableParams={getDownloadFetchParams()}
      businessType={`${entityType}-stats-countries-compare`}
      isDisableDownload={loading}
      isShowMaxDownloadRowsTips={false}
      downloadModalSize={820}
      isDisableDataAmount={true}
      newStyleFileName={`Game_Comparison`}
      pageId={TopchartPageId.PCConsoleMetricsTable}
      useWebDownload={true}
      downloadKey={'pcConsoleGameNew'}
      downloadLogConfigKey={'CompareMultidimensionDownloadButton'}
    />
  );
  return (
    <div className="dbi-trends-card dbi-kpi-trends-card">
      <CardSlotV2
        className={
          'dbi-mobileGames-card-context dbi-mobileGames-title-custom dbi-jst-new-pcconsole'
        }
        title={formatMessage({ id: 'dbi_game_comparison_table' })}
        titleStyle={{ lineHeight: '20px' }}
        activeTab={''}
        alwaysShowIconList
        iconListExtra={pageSortTable?.length > 0 ? exportTableCom : null}
        iconList={[]}
      >
        {pageSortTable?.length > 0 ? (
          <TopchartTable
            // recordKey="market"
            className="dbi-mobileGames-details-table"
            loading={loading}
            records={pageSortTable.map((item: any, idx: number) => ({
              ...item,
              idx,
            }))}
            tableTotal={tableTotal}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPagingChange={onPagingChange}
            pageSizeOptions={pageSizeOptions}
            // scrollMaxHeight={'calc(100vh - 290px)'}
            columns={getAllColumns()}
            tableAddons={[
              pageable({
                pageSize,
                stateTextVisible: false,
                pageSizeVisible: false,
                pageIndexVisible: false,
                jumpVisible: false,
                endJumpVisible: false,
              }),
            ]}
          />
        ) : (
          <div className={'dbi-new-pc-console-no-data'}>
            <NoData />
            {/* <span*/}
            {/*  style={{ textAlign: 'center' }}*/}
            {/*  dangerouslySetInnerHTML={{*/}
            {/*    __html: formatMessage({ id: 'dbi_info_allMetrics_nodata_msg_new' }),*/}
            {/*  }}*/}
            {/* ></span>*/}
          </div>
        )}
      </CardSlotV2>
      <Modal visible={modalVisible} onClose={() => setModalVisible(false)}>
        <Modal.Body>
          <div className={'dbi-new-compare-modal-context'}>
            {formatMessage({ id: 'dbi_top_compare_modal_context' })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleConfirm} type={'primary'}>
            {formatMessage({ id: 'common_confirm' })}
          </Button>
          <Button onClick={() => setModalVisible(false)}>
            {formatMessage({ id: 'common_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TableModule;
